<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { ref, computed, watch, useTemplateRef } from 'vue'
import { useFocusWithin } from '@vueuse/core'
import { type JawgSuggestedFeature } from '../../../../interfaces/map'
import { debounce } from '../../../../utils/shared/debounce'

const {
    icon = 'IconSearch',
    alreadySearchedAddresses,
    suggestedAddresses,
    isAddressSelected = false,
    isRouteCheckpoint = false
} = defineProps<{
    icon?: string
    alreadySearchedAddresses?: JawgSuggestedFeature[]
    suggestedAddresses?: JawgSuggestedFeature[]
    isAddressSelected?: boolean
    placeholder?: string
    isRouteCheckpoint?: boolean
}>()

const emits = defineEmits<{
    selectAddress: [address: JawgSuggestedFeature]
    updateAddressQuery: [query: string]
    openRoutePanel: []
}>()

const addressInputSearch = defineModel<string>({
    default: ''
})

const inputRef = useTemplateRef<HTMLInputElement>('inputRef')
const addressDropdown = useTemplateRef<HTMLDivElement>('addressDropdown')
const { focused } = useFocusWithin(inputRef)
const showSuggestedAddresses = ref(false)

const filteredSavedAddresses = computed<JawgSuggestedFeature[]>(() => {
    const query = addressInputSearch.value.trim().toLowerCase()
    if (!query) return alreadySearchedAddresses || []
    return (alreadySearchedAddresses || []).filter((addr) =>
        addr.properties.label.toLowerCase().includes(query)
    )
})

const dropdownAddresses = computed<JawgSuggestedFeature[]>(() => {
    if (addressInputSearch.value.length > 0) {
        const suggested = (suggestedAddresses || []).filter(
            (sa) =>
                !filteredSavedAddresses.value.some(
                    (saved) => saved.properties.label === sa.properties.label
                )
        )
        return [...filteredSavedAddresses.value, ...suggested]
    }
    return alreadySearchedAddresses || []
})

const onAddressSelected = (address: JawgSuggestedFeature) => {
    emits('selectAddress', address)
    addressInputSearch.value = address.properties.label
    showSuggestedAddresses.value = false
}

const updateAddressDebounced = debounce((query: string) => {
    emits('updateAddressQuery', query)
}, 1000)

const clearAddressInput = () => {
    addressInputSearch.value = ''
    emits('updateAddressQuery', '')
}

const onInputClick = () => {
    if (
        addressInputSearch.value.length === 0 &&
        (alreadySearchedAddresses || []).length > 0
    ) {
        showSuggestedAddresses.value = true
    }
}

watch(
    () => suggestedAddresses,
    (newList) => {
        if (
            addressInputSearch.value.length > 0 &&
            (newList || []).length > 0 &&
            focused.value
        ) {
            showSuggestedAddresses.value = true
        }
    }
)

onClickOutside(addressDropdown, () => {
    showSuggestedAddresses.value = false
})
</script>

<template>
    <div
        class="overflow-y-auto items-start w-full"
        :class="!isRouteCheckpoint && 'min-w-[174px] md:w-fit md:min-w-[460px]'"
    >
        <div
            ref="inputRef"
            class="flex w-full h-11 md:h-12 items-center justify-between gap-2 border p-2 md:p-3 cursor-pointer"
            :class="[
                focused
                    ? 'border-light-blue-primary bg-primary-light'
                    : 'border-zinc-300 bg-white',
                isRouteCheckpoint ? 'rounded-lg ' : 'rounded-full'
            ]"
        >
            <div class="flex">
                <component
                    :is="icon"
                    class="size-5 md:size-6"
                    :class="focused ? 'text-sky-400' : 'text-zinc-400'"
                />
            </div>

            <input
                :value="addressInputSearch"
                type="text"
                :placeholder="
                    placeholder || $t('map.search_address.placeholder')
                "
                class="font-normal focus:outline-none w-full text-[15px] leading-[18px]"
                :class="
                    focused
                        ? 'text-primary-info bg-primary-light placeholder:text-primary-info'
                        : 'text-gray-l-800 placeholder:text-zinc-500'
                "
                @click="onInputClick"
                @input="
                    (e: Event) => {
                        addressInputSearch = (e.target as HTMLInputElement)
                            .value
                        updateAddressDebounced(addressInputSearch)
                    }
                "
            />
            <div class="flex">
                <IconXCircle
                    v-if="addressInputSearch.length > 0"
                    class="size-5"
                    :class="[
                        focused ? 'text-sky-400' : 'text-zinc-400',
                        isAddressSelected && 'md:translate-x-2'
                    ]"
                    is-solid
                    @click="clearAddressInput"
                />
            </div>

            <div
                v-if="isAddressSelected"
                class="flex items-center h-8 md:h-10 gap-2 rounded-3xl px-[10px] md:px-4 py-2 bg-sky-500 md:translate-x-2"
                @click="emits('openRoutePanel')"
            >
                <span class="hidden md:block text-white leading-tight">
                    {{ $t('map.search_address.route') }}
                </span>
                <IconMapPin is-solid class="size-5 md:size-6 text-white" />
            </div>
        </div>

        <div
            v-show="showSuggestedAddresses && dropdownAddresses.length > 0"
            ref="addressDropdown"
            class="absolute top-14 md:mt-1 px-4 py-2 md:py-4 gap-2 rounded-2xl md:rounded-3xl border border-zinc-300 bg-white shadow-lower-3 overflow-y-auto z-20"
            :class="
                isRouteCheckpoint
                    ? 'w-full'
                    : ' max-w-[310px] md:w-[460px] md:max-w-[460px]'
            "
        >
            <button
                v-for="(address, index) in dropdownAddresses"
                :key="index"
                type="button"
                class="relative flex w-full items-center px-1 md:px-2 py-4 gap-2 md:gap-4 after:block after:w-full after:h-px after:bg-zinc-200 after:absolute after:bottom-0 after:left-0 hover:bg-zinc-100 last:after:hidden"
                :class="dropdownAddresses.length === 1 && 'md:py-0'"
                @click="onAddressSelected(address)"
            >
                <IconClock
                    v-if="alreadySearchedAddresses?.includes(address)"
                    class="size-6 text-zinc-500"
                />
                <IconMapPin v-else class="size-6 text-zinc-500" />
                <span
                    class="text-[15px] font-medium leading-[18px] text-zinc-600 truncate"
                >
                    {{ address.properties.label }}
                </span>
            </button>
        </div>
    </div>
</template>
